<template>
  <div class="view-faqs" data-cy="faqs-sections">
    <p class="title" data-cy="faqs-title">
      {{ $t('viewFaqs.title') }}
    </p>
    <Accordion :list="accordionList" :selected-group-id="selectedGroupId" />
  </div>
</template>
<script lang="ts">
import Accordion from '@/components/common/accordion.vue';
import { globalConstants } from '@/constants/global.constants';
import { AccordionGroup } from '@/models/accordion-group.model';
import { type FaqSectionModel } from '@/models/faq-section.model';
import { type FaqModel } from '@/models/faq.model';
import { FaqsService } from '@/services/faqs.service';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { type Entry } from 'contentful';
import { defineComponent } from 'vue';
import { useLocale } from '@/composables/locale';
import type { FaqImage } from '@/models/faq-image.model';
import { useRoute } from 'vue-router';

export default defineComponent({
  name: 'view-faqs',
  components: {
    Accordion,
  },
  inheritAttrs: false,
  setup() {
    const { userLanguage } = useLocale();
    const route = useRoute();

    return { userLanguage, route };
  },
  data() {
    return {
      faqsService: new FaqsService(),
      faqSections: new Array<FaqSectionModel>(),
      faqs: new Array<FaqModel>(),
    };
  },
  computed: {
    selectedGroupId(): string {
      return this.route.params['id'] as string;
    },
    accordionList(): Array<AccordionGroup> {
      const accordionList = new Array<AccordionGroup>();

      this.faqSections.forEach((section) => {
        const itemIds = section.faqs.map((item) => item.externalId);
        const filteredFaqs = this.faqs.filter((faq) => itemIds.includes(faq.externalId));
        const currentLocale = this.userLanguage;
        const fallbackLocale = globalConstants.fallBackLocale;

        const items = filteredFaqs.map((item) => {
          const description = item.description[currentLocale] ?? item.description[fallbackLocale];
          const documentToHtmlStringDescription = documentToHtmlString(description);
          const rawImages = item.images?.[currentLocale] ?? item.images?.[fallbackLocale];

          return {
            id: item.externalId,
            title: item.title[currentLocale] || item.title[fallbackLocale],
            body: documentToHtmlStringDescription,
            images: rawImages ? this.getImages(rawImages) : [],
            isOpen: false,
          };
        });

        const group: AccordionGroup = {
          id: section.externalId,
          title: section.title[currentLocale] || section.title[fallbackLocale],
          items,
        };

        accordionList.push(group);
      });

      return accordionList;
    },
  },
  mounted() {
    this.fetchFaqsFromServer();
  },
  methods: {
    async fetchFaqsFromServer() {
      const faqSectionsResult = await this.faqsService.getFaqSections();

      if (faqSectionsResult.model) {
        this.faqSections = faqSectionsResult.model;
      }

      const faqsResult = await this.faqsService.getFaqs();

      if (faqsResult.model) {
        this.faqs = faqsResult.model;
      }
    },
    getImages(images: Entry<any>[]): FaqImage[] {
      const currentLocale = this.userLanguage as string;
      const fallbackLocale = globalConstants.fallBackLocale as any;

      return images?.map((image: any) => ({
        id: image.sys.id,
        url: image.fields.file[`${currentLocale}`]?.url || image.fields.file[fallbackLocale]?.url,
        description: image.fields.description[currentLocale] || image.fields.description[fallbackLocale],
        title: image.fields.title[currentLocale] || image.fields.title[fallbackLocale],
      }));
    },
  },
});
</script>

<style scoped lang="scss">
.view-faqs {
  @include common-container;

  margin-bottom: 40px;

  .title {
    text-align: left;
    font-size: 20px;
    margin: 16px 0;
  }
}
</style>
